<template>
  <section id="home" class="hero-section-v2 relative">
    <!-- Main Content - Centered Layout -->
    <div class="relative w-full max-w-none mx-auto px-4 sm:px-8 lg:px-12 xl:px-16 flex flex-col items-center gap-[56px] pt-20 md:pt-24 lg:pt-28 pb-8 md:pb-9 lg:pb-20" style="z-index: 1;">
      <!-- Logo - Separate element at the top -->
      <div class="relative w-14 h-14 lg:w-20 lg:h-20 flex items-center justify-center">
        <img 
          :src="logoSrc" 
          :alt="brandName"
          class="h-full w-full object-contain"
        />
      </div>
      
      <!-- Title - 2 lines -->
      <div class="flex flex-col items-center gap-4 md:gap-6 text-center">
        <h1 class="text-[24px] sm:text-[28px] md:text-[32px] lg:text-[40px] font-bold text-[#200d5f] leading-[1.2] tracking-[-0.8px]">
          {{ heroTitleLine1 }}
        </h1>
        <div class="flex flex-col items-center gap-0">
          <h2 class="text-[48px] sm:text-[56px] md:text-[64px] lg:text-[96px] font-black leading-[1] bg-clip-text text-transparent bg-gradient-to-r from-[#892DDE] via-[#4A1878] via-[#AA57F8] via-[#B448C2] to-[#641291] whitespace-pre-wrap">
            {{ heroTitleLine2 }}
          </h2>
          <!-- Dashed stroke border under HiCHAT AI -->
          <img 
            :src="strokeBorderSrc" 
            alt="Stroke border"
            class="hero-title-stroke"
          />
        </div>
      </div>
      
      <!-- Description -->
      <p class="text-sm sm:text-base md:text-base lg:text-lg font-medium text-[#36393e] text-center max-w-[842px] leading-6 md:leading-7 lg:leading-[28px]">
        {{ heroDescription }}
      </p>
      
      <!-- Buttons -->
      <div class="flex flex-row gap-4 md:gap-5 items-center">
        <a 
          :href="primaryCtaUrl" 
          class="inline-flex items-center justify-center gap-2 h-10 md:h-12 px-4 md:px-5 py-2 md:py-2.5 rounded-full text-sm md:text-base lg:text-lg font-bold text-white bg-gradient-to-r from-hilab to-[#4A1878] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] transition-all duration-200 hover:from-[#7a26c5] hover:to-[#3d1563] hover:shadow-[0px_6px_8px_0px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 active:translate-y-0"
        >
          {{ heroPrimaryLabel }}
        </a>
        <a 
          :href="secondaryCtaUrl" 
          class="inline-flex items-center justify-center gap-2 h-10 md:h-12 px-4 md:px-5 py-2 md:py-2.5 rounded-full text-sm md:text-base lg:text-lg font-bold text-hilab border border-hilab shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] transition-all duration-200 hover:bg-hilab/5 hover:-translate-y-0.5 active:translate-y-0"
        >
          <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {{ heroSecondaryLabel }}
        </a>
      </div>
      
      <!-- Hero Thumbnail Image -->
      <div class="relative w-full max-w-[1200px]">
        <img 
          :src="thumbnailSrc" 
          alt="HiCHAT Dashboard"
          class="w-full h-auto object-contain"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  brandName: {
    type: String,
    required: true,
  },
  logoUrl: {
    type: String,
    default: '',
  },
  primaryCtaUrl: {
    type: String,
    required: true,
  },
  secondaryCtaUrl: {
    type: String,
    required: true,
  },
  demoUrl: {
    type: String,
    default: '/app/accounts/new',
  },
  content: {
    type: Object,
    default: () => ({}),
  },
  images: {
    type: Array,
    default: () => [],
  },
});

const defaultLogo = '/brand-assets/logo_notext_v2_gradient.svg';
// Ignore logoUrl prop from parent, always use defaultLogo for hero section
const logoSrc = computed(() => defaultLogo);

const strokeBorderSrc = '/landing/stroke-border.svg';
const thumbnailSrc = '/landing/thumnail_bg_removed.png';

const hero = computed(() => props.content || {});

// Map badge to title_line1 (first line of title)
const heroTitleLine1 = computed(() => hero.value.badge || hero.value.title_line1 || 'Nâng Tầm Trải Nghiệm Khách Hàng Với');
// Map title to title_line2 (second line of title)
const heroTitleLine2 = computed(() => hero.value.title || hero.value.title_line2 || 'HiCHAT AI');
const heroDescription = computed(
  () =>
    hero.value.description ||
    'Đừng để khách hàng phải chờ đợi. HiCHAT giúp doanh nghiệp của bạn phản hồi chính xác 24/7, hợp nhất mọi kênh liên lạc và tăng tỷ lệ chuyển đổi ngay lập tức.'
);
const heroPrimaryLabel = computed(() => hero.value.primary_cta_label || 'Dùng thử MIỄN PHÍ ngay hôm nay');
const heroSecondaryLabel = computed(() => hero.value.secondary_cta_label || 'Xem video Demo');
</script>

